"use client";

import type { ReactNode, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useOsLayout } from "@/app/(saas)/(app)/(os)/_components/os-layout-context";
import {
  FlowQuestionProgress,
  type FlowJourneyProgress,
} from "./flow-progress";
import { FlowActionBar } from "./flow-action-bar";
import { useFlowKeyboardInset } from "./use-flow-keyboard-inset";
import { FlowWizardHeader } from "./flow-wizard-header";
import type { PortalBrandProps } from "./portal-brand";

export type FlowContentWidth = "default" | "narrow";
export type FlowFooterPlacement = "fixed" | "inline";
export type FlowProgressVariant = "branded" | "inline" | "section";
export type FlowWizardBrand = Pick<
  PortalBrandProps,
  "name" | "avatarUrl" | "subtitle"
> & {
  licenseNumber?: string | null;
  logoUrl?: string | null;
  logoAlt?: string | null;
  licenseBadge?: string | null;
};

type FlowWizardShellProps = {
  title: string;
  /** Embedded calculator, standalone page, or the existing overlay wizard. */
  presentation?: "overlay" | "embedded" | "page" | "dialog";
  titleBadge?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  stepIndicator?: ReactNode;
  /** Segmented header progress. Pair with `progressVariant`. */
  journeyProgress?: FlowJourneyProgress | null;
  /**
   * branded = logo + % (questionnaires).
   * inline = quiet title + "1 of 4" (OS onboarding, when the page already names the step).
   */
  progressVariant?: FlowProgressVariant;
  /** Section label shown above the standard percentage progress bar. */
  progressSection?: string;
  footer?: ReactNode;
  children: ReactNode;
  fullBleed?: boolean;
  stepDescId?: string;
  stepDescription?: string;
  headerAction?: ReactNode;
  className?: string;
  /**
   * Render into document.body so OS chrome/sidebar cannot clip the wizard.
   * Useful for nested OS routes (eSign prepare, template create).
   */
  portal?: boolean;
  /** Lock OS main-area scroll while open (no-op outside OS layout). */
  lockOsScroll?: boolean;
  /** Content column width when not fullBleed. */
  contentWidth?: FlowContentWidth;
  /** Label a text-only scroll area and make it keyboard reachable. */
  contentLabel?: string;
  /** fixed = overlay footer (onboarding); inline = in-flow footer (eSign). */
  footerPlacement?: FlowFooterPlacement;
  /** Compact title for dense prepare headers. */
  titleSize?: "default" | "compact";
  /** Public portal identity shown instead of an internal form title. */
  brand?: FlowWizardBrand;
  /** Keep the Venturu mark in the header while using a non-branded progress style. */
  showLogo?: boolean;
  /** Lead = tighter mobile-first public form chrome. */
  chrome?: "default" | "lead";
};

export function FlowWizardShell({
  title,
  presentation = "overlay",
  titleBadge,
  onClose,
  closeLabel = "Close",
  stepIndicator,
  journeyProgress,
  progressVariant = "branded",
  progressSection,
  footer,
  children,
  fullBleed,
  stepDescId,
  stepDescription = "Complete the steps in this flow.",
  headerAction,
  className,
  portal = false,
  lockOsScroll = false,
  contentWidth = "default",
  contentLabel,
  footerPlacement = "fixed",
  titleSize = "default",
  brand,
  showLogo = false,
  chrome = "default",
}: FlowWizardShellProps) {
  const hasJourney = Boolean(journeyProgress);
  const branded = hasJourney && progressVariant === "branded";
  const inlineProgress = hasJourney && progressVariant === "inline";
  const sectionProgress = hasJourney && progressVariant === "section";
  const osLayout = useOsLayout();
  const [mounted, setMounted] = useState(false);
  const fixedFooter =
    Boolean(footer) &&
    footerPlacement === "fixed" &&
    (presentation === "overlay" || presentation === "page");
  const keyboardInset = useFlowKeyboardInset(fixedFooter);
  const style = {
    "--flow-keyboard-inset": `${keyboardInset}px`,
  } as CSSProperties;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!lockOsScroll) return;
    osLayout?.setMainAreaScrollLocked(true);
    return () => osLayout?.setMainAreaScrollLocked(false);
  }, [lockOsScroll, osLayout]);

  const contentPadBottom =
    fixedFooter && !fullBleed
      ? chrome === "lead"
        ? "pb-[calc(6rem+env(safe-area-inset-bottom)+var(--flow-keyboard-inset,0px))]"
        : "pb-[calc(7rem+env(safe-area-inset-bottom)+var(--flow-keyboard-inset,0px))]"
      : undefined;

  const shell = (
    <div
      data-flow-presentation={presentation}
      data-flow-chrome={chrome}
      style={style}
      className={cn(
        "group/flow flex flex-col bg-background",
        presentation === "overlay"
          ? "fixed inset-0 z-50"
          : presentation === "page"
            ? "min-h-[100dvh]"
            : presentation === "dialog"
              ? "h-full min-h-0 overflow-hidden"
              : "min-h-[28rem] py-4 sm:py-6",
        portal && "h-[100dvh] max-h-[100dvh]",
        className,
      )}
    >
      <div
        className={
          presentation === "embedded" || chrome === "lead"
            ? "mx-auto w-full max-w-2xl"
            : presentation === "page" || presentation === "dialog"
              ? "mx-auto w-full max-w-4xl"
              : undefined
        }
      >
        <FlowWizardHeader
          title={title}
          headingLevel={presentation === "embedded" ? "h2" : "h1"}
          titleBadge={titleBadge}
          onClose={onClose}
          closeLabel={closeLabel}
          stepIndicator={stepIndicator}
          headerAction={headerAction}
          brand={brand}
          showLogo={
            showLogo || presentation === "page" || presentation === "dialog"
          }
          branded={branded}
          inlineProgress={inlineProgress}
          titleSize={
            presentation === "page" || presentation === "dialog"
              ? "compact"
              : titleSize
          }
          chrome={chrome}
          journeyProgress={journeyProgress}
        />
      </div>

      {sectionProgress ? (
        <div className="shrink-0 border-b bg-background px-4 py-3 sm:px-6">
          <div className="mx-auto w-full max-w-2xl">
            <FlowQuestionProgress
              current={journeyProgress?.current ?? 0}
              total={journeyProgress?.total ?? 0}
              section={progressSection}
              estimate={`${journeyProgress?.percentage ?? 0}%`}
            />
          </div>
        </div>
      ) : null}

      {stepDescId ? (
        <p id={stepDescId} className="sr-only">
          {stepDescription}
        </p>
      ) : null}

      <section
        data-flow-content
        aria-label={contentLabel ?? title}
        tabIndex={contentLabel ? 0 : undefined}
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          fullBleed
            ? "overflow-hidden"
            : (presentation === "overlay" || presentation === "dialog") &&
                "overflow-y-auto overscroll-contain",
        )}
      >
        {fullBleed ? (
          children
        ) : contentWidth === "narrow" ? (
          <div
            className={cn(
              "mx-auto w-full max-w-2xl flex-1",
              chrome === "lead" ? "px-4 py-6 sm:px-6 sm:py-8" : "px-6 py-8",
              presentation === "page" && chrome !== "lead" && "py-10 sm:py-16",
              contentPadBottom,
            )}
          >
            {children}
          </div>
        ) : (
          <div
            className={cn(
              "container flex-1",
              chrome === "lead" ? "px-4 pt-6 sm:px-6 sm:pt-8" : "px-6 pt-8",
              presentation === "page" && chrome !== "lead" && "py-10 sm:py-16",
              contentPadBottom,
            )}
          >
            <div className="mx-auto w-full md:w-2/3">{children}</div>
          </div>
        )}
      </section>

      {footer ? (
        <FlowActionBar
          fixed={fixedFooter}
          embedded={presentation === "embedded"}
          lead={chrome === "lead"}
          inset={keyboardInset}
        >
          {footer}
        </FlowActionBar>
      ) : null}
    </div>
  );

  if (portal) {
    if (!mounted || typeof document === "undefined") return null;
    return createPortal(shell, document.body);
  }

  return shell;
}
